/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions */
import { useRef } from 'react';
import isShallowEqual from '@atlassian/jira-common-util-is-shallow-equal/src/index.tsx';

const STABLE_EMPTY_OBJECT = Object.freeze({});
const STABLE_EMPTY_ARRAY: any = Object.freeze([]);

/**
 * referential equality check stability helper
 * @returns a "stable" empty object (always the same reference)
 */
export const getStableEmptyObject = <T extends Record<string, unknown>>(): T =>
	STABLE_EMPTY_OBJECT as any;
/**
 * referential equality check stability helper
 * @returns a "stable" empty array (always the same reference)
 */
export const getStableEmptyArray = <T extends any[]>(): T => STABLE_EMPTY_ARRAY as any;

/**
 * referential equality check stability helper
 * @returns a stable empty object if the value is an empty object
 */
export const withStableEmptyObjectAsFallback = <T extends Record<string, unknown>>(value: T): T =>
	Object.keys(value).length === 0 ? (STABLE_EMPTY_OBJECT as T) : value;

/**
 * referential equality check stability helper
 * @returns a stable empty array if the value is an empty array
 */
export const withStableEmptyArrayAsFallback = <T extends unknown[]>(value: T): T =>
	value.length === 0 ? (STABLE_EMPTY_ARRAY as T) : value;

/**
 * referential equality check stability helper
 * While "props" of react object are unstable by design, this hook provides a stable reference to the props object.
 * Is not defined for props with children, as children are mostly not stable.
 *
 * Based on shallow equality check.
 *
 * @returns reference to an old object if it is shallow equal to the new one
 */
export function useShallowStableReference<T>(props: T, isEnabled = true): T {
	const ref = useRef(props);

	if (!isEnabled) {
		return props;
	}

	if (!isShallowEqual(props, ref.current)) {
		ref.current = props;
	}
	return ref.current;
}
